import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import LoginIcon from '@mui/icons-material/Login';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import { AppBar, Avatar, Box, Divider, IconButton, List, ListItemButton, ListItemIcon, ListItemText, useTheme } from '@mui/material';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { profile } from '../assests/icons';
import LangMenu from '../components/LangMenu';
import AppButton from '../components/atoms/AppButton';
import { useAppSelector } from '../redux/hooks';
import { selectIsLoggedIn, selectUser } from '../redux/slices/user';
import { changeAppLanguage } from '../services/localization/i18n';
import LayoutStyle from './Layout.style';
import ProfileMenu from '../components/ProfileMenu';
import ProfileMenuItem from '../components/ProfileMenuItem';
import { CurrencySelector } from '../components/CurrencySelector';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { getCountryIcon } from '../utils/commonUtils';

// Utility for consistent drawer icon styling
const getDrawerIconStyle = (color: string) => ({
    bgcolor: color,
    color: 'white',
    fontSize: 20,
    padding: 0.3,
    borderRadius: 2
});

type Anchor = 'left' | 'right' | 'top' | 'bottom';

interface AppDrawerProps {
    anchor: Anchor;
    open: boolean;
    onClose: () => void;
    handleRegister: () => void;
    handleDarkMode: () => void;
    handleOtp: () => void;
    isLogined: boolean;
}

const AppDrawer: React.FC<AppDrawerProps> = ({ onClose, handleDarkMode, isLogined }) => {
    const navigation = useNavigate();
    const { t } = useTranslation();
    const theme = useTheme();

    const userInfo = useAppSelector(selectUser);
    const isLoggedIn = useAppSelector(selectIsLoggedIn);

    const [anchorLang, setAnchorLang] = React.useState<null | HTMLElement>(null);

    const handleLanguage = (e: React.MouseEvent<HTMLElement>) => setAnchorLang(e.currentTarget);
    const open = Boolean(anchorLang);

    const sellText = t('web.navbar-sell');
    const activitiesText = t('web.navbar-trade');

    const changeLanguage = async (lng: string | undefined) => {
        if (!lng) return;
        await changeAppLanguage(lng);
        setAnchorLang(null);
    };

    const goToLogin = () => {
        navigation('/login');
        onClose();
    };

    // Guarded navigation helper - handles auth check and cleanup
    const guardedNavigate = (path: string) => {
        if (!isLoggedIn) {
            goToLogin();
        } else {
            navigation(path);
            onClose();
        }
    };

    const handleSell = () => guardedNavigate('/Additem');
    const handleActivities = () => guardedNavigate('/activities');

    const handleClickProfile = (event: React.MouseEvent<HTMLElement>) => {
        if (isLoggedIn) {
            onClose();
            navigation('/myprofile');
        } else {
            goToLogin();
        }
    };
    const flagIcon = getCountryIcon();
    return (
        <>
            <Box sx={LayoutStyle.drawer.container} role="presentation" onKeyDown={onClose}>
                <AppBar position="static" elevation={0}>
                    <Toolbar sx={LayoutStyle.drawer.toolbar}>
                        <Box display={'flex'} alignItems={'center'}>
                            <Box component={'img'} src={require('./../assests/images/icon_presign.png')} sx={LayoutStyle.drawer.logoImg} />
                            <Typography sx={LayoutStyle.drawer.logaText}>UZOFFER</Typography>
                        </Box>
                        <Box display={'flex'} alignItems={'center'}>
                            <Avatar onClick={handleClickProfile} sx={LayoutStyle.drawer.avatar} src={!!userInfo && !!userInfo.photoUrl ? userInfo.photoUrl : profile} />
                            <AppButton
                                variant={'text'}
                                onClick={handleLanguage}
                                btnStyle={{ bgcolor: 'transparency' }}
                                middleIcon={<Box component="img" src={flagIcon} sx={{ width: 22, height: 'auto' }} />}
                            />

                            <IconButton onClick={handleDarkMode} sx={{ color: 'text.dark', bgcolor: 'transparency' }}>
                                {theme.palette.mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
                            </IconButton>
                            <CurrencySelector />
                            <CloseIcon onClick={onClose} sx={LayoutStyle.drawer.closeIcon} color="action" />
                        </Box>
                    </Toolbar>
                </AppBar>

                <Box sx={LayoutStyle.drawer.body}>
                    <List>
                        {isLogined && <ProfileMenuItem key={sellText} icon={<AddIcon sx={getDrawerIconStyle('#4E4E4D')} />} label={sellText} onClick={handleSell} hasDivider={false} />}

                        {isLogined && (
                            <ProfileMenuItem key={activitiesText} icon={<SyncAltIcon sx={getDrawerIconStyle('#65C466')} />} label={activitiesText} onClick={handleActivities} hasDivider={false} />
                        )}

                        {!isLogined && (
                            <ListItemButton onClick={goToLogin} sx={{ py: 0.5 }}>
                                <ListItemIcon>
                                    <LoginIcon sx={getDrawerIconStyle('#EB4E3D')} />
                                </ListItemIcon>
                                <ListItemText primary={'login'} />
                            </ListItemButton>
                        )}

                        {isLogined && <ProfileMenu handleClose={onClose} />}
                    </List>

                    <Divider />
                </Box>
            </Box>

            <LangMenu open={open} onClose={changeLanguage} anchorEl={anchorLang} />
        </>
    );
};

// Fixed memoization logic - only skip re-render when ALL props are the same
export default React.memo(AppDrawer, (prevProps, nextProps) => {
    return prevProps.open === nextProps.open && prevProps.isLogined === nextProps.isLogined;
});
